import type { EmbeddedNavigationGroup } from '@/types/embeddedApplication';
import { navigationTreeToEmbeddedGroups as toEmbeddedGroups } from '@/navigation/NavigationTreeBuilder';
import type { NavigationNode } from '@/types/navigationConfig';

export function navigationTreeToEmbeddedGroups(
  nodes: NavigationNode[],
): EmbeddedNavigationGroup[] {
  return toEmbeddedGroups(nodes);
}

export function flattenNavigationTree(
  nodes: NavigationNode[],
  depth = 0,
  parentExpanded = true,
): Array<{ node: NavigationNode; depth: number; visible: boolean }> {
  const rows: Array<{ node: NavigationNode; depth: number; visible: boolean }> =
    [];

  nodes.forEach((node) => {
    rows.push({ node, depth, visible: parentExpanded });

    if (node.children.length > 0) {
      rows.push(
        ...flattenNavigationTree(node.children, depth + 1, parentExpanded),
      );
    }
  });

  return rows;
}

export function findNavigationNode(
  nodes: NavigationNode[],
  nodeId: string,
): NavigationNode | undefined {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return node;
    }

    const childMatch = findNavigationNode(node.children, nodeId);

    if (childMatch) {
      return childMatch;
    }
  }

  return undefined;
}

export function updateNavigationNode(
  nodes: NavigationNode[],
  nodeId: string,
  updater: (node: NavigationNode) => NavigationNode,
): NavigationNode[] {
  return nodes.map((node) => {
    if (node.id === nodeId) {
      return updater(node);
    }

    if (node.children.length > 0) {
      return {
        ...node,
        children: updateNavigationNode(node.children, nodeId, updater),
      };
    }

    return node;
  });
}

export function deleteNavigationNode(
  nodes: NavigationNode[],
  nodeId: string,
): NavigationNode[] {
  return nodes
    .filter((node) => node.id !== nodeId)
    .map((node) => ({
      ...node,
      children: deleteNavigationNode(node.children, nodeId),
    }));
}

export function collectNodeIds(nodes: NavigationNode[]): string[] {
  return nodes.flatMap((node) => [node.id, ...collectNodeIds(node.children)]);
}

export function validateNavigationTree(nodes: NavigationNode[]): string[] {
  const issues: string[] = [];
  const routes = new Set<string>();

  const walk = (items: NavigationNode[], parentLabel?: string) => {
    const labels = new Set<string>();

    items.forEach((node) => {
      if (!node.label.trim()) {
        issues.push(`navigationConfig.validation.labelRequired:${node.id}`);
      }

      if (parentLabel && labels.has(node.label)) {
        issues.push(`navigationConfig.validation.duplicateLabel:${node.id}`);
      }

      labels.add(node.label);

      if (node.type === 'item') {
        if (!node.route?.trim()) {
          issues.push(`navigationConfig.validation.routeRequired:${node.id}`);
        } else if (routes.has(node.route)) {
          issues.push(`navigationConfig.validation.duplicateRoute:${node.id}`);
        } else {
          routes.add(node.route);
        }
      }

      if (node.children.length > 0) {
        walk(node.children, node.label);
      }
    });
  };

  walk(nodes);
  return issues;
}

function cloneTree(nodes: NavigationNode[]): NavigationNode[] {
  return structuredClone(nodes);
}

function removeNode(nodes: NavigationNode[], nodeId: string): NavigationNode[] {
  return nodes
    .filter((node) => node.id !== nodeId)
    .map((node) => ({
      ...node,
      children: removeNode(node.children, nodeId),
    }));
}

function insertNode(
  nodes: NavigationNode[],
  targetId: string,
  nodeToInsert: NavigationNode,
  position: 'before' | 'after' | 'inside',
): NavigationNode[] {
  const result: NavigationNode[] = [];

  for (const node of nodes) {
    if (node.id === targetId) {
      if (position === 'before') {
        result.push(nodeToInsert, node);
        continue;
      }

      if (position === 'after') {
        result.push(node, nodeToInsert);
        continue;
      }

      result.push({
        ...node,
        children: [...node.children, { ...nodeToInsert, parentId: node.id }],
      });
      continue;
    }

    result.push({
      ...node,
      children: insertNode(node.children, targetId, nodeToInsert, position),
    });
  }

  return result;
}

export function moveNavigationNode(
  nodes: NavigationNode[],
  dragId: string,
  targetId: string,
  position: 'before' | 'after' | 'inside',
): NavigationNode[] {
  if (dragId === targetId) {
    return nodes;
  }

  const draggedNode = findNavigationNode(nodes, dragId);

  if (!draggedNode) {
    return nodes;
  }

  const treeWithoutNode = removeNode(cloneTree(nodes), dragId);
  const movedNode = { ...draggedNode, parentId: null };

  if (position === 'inside') {
    return insertNode(treeWithoutNode, targetId, movedNode, 'inside');
  }

  return insertNode(treeWithoutNode, targetId, movedNode, position);
}

export function createNavigationNode(
  type: NavigationNode['type'],
  parentId: string | null,
): NavigationNode {
  const id = `${type}-${crypto.randomUUID()}`;

  return {
    id,
    parentId,
    label:
      type === 'group'
        ? 'navigationConfig.nodes.newGroup'
        : 'navigationConfig.nodes.newItem',
    icon: type === 'group' ? 'folder' : 'apps',
    route: type === 'item' ? '/new-route' : undefined,
    type,
    visibility: 'visible',
    displayOrder: 1,
    openMode: 'sameTab',
    children: [],
  };
}

export function appendNavigationNode(
  nodes: NavigationNode[],
  node: NavigationNode,
  parentId: string | null,
): NavigationNode[] {
  if (!parentId) {
    return [...nodes, node];
  }

  return updateNavigationNode(nodes, parentId, (parent) => ({
    ...parent,
    children: [...parent.children, { ...node, parentId }],
  }));
}

function regenerateNodeIds(
  node: NavigationNode,
  parentId: string | null,
): NavigationNode {
  const id = `${node.type}-${crypto.randomUUID()}`;

  return {
    ...node,
    id,
    parentId,
    children: node.children.map((child) => regenerateNodeIds(child, id)),
  };
}

export function duplicateNavigationNode(
  nodes: NavigationNode[],
  nodeId: string,
): NavigationNode[] {
  const sourceNode = findNavigationNode(nodes, nodeId);

  if (!sourceNode) {
    return nodes;
  }

  const duplicate = regenerateNodeIds(
    structuredClone(sourceNode),
    sourceNode.parentId,
  );

  return appendNavigationNode(nodes, duplicate, sourceNode.parentId);
}

export function collectSiblingLabels(
  nodes: NavigationNode[],
  nodeId: string,
): string[] {
  const findSiblings = (items: NavigationNode[]): string[] | null => {
    for (const item of items) {
      if (item.id === nodeId) {
        return items
          .filter((sibling) => sibling.id !== nodeId)
          .map((sibling) => sibling.label);
      }

      const fromChildren = findSiblings(item.children);

      if (fromChildren) {
        return fromChildren;
      }
    }

    return null;
  };

  return findSiblings(nodes) ?? [];
}

export function collectAllRoutes(nodes: NavigationNode[]): string[] {
  const routes: string[] = [];

  const walk = (items: NavigationNode[]) => {
    items.forEach((node) => {
      if (node.type === 'item' && node.route) {
        routes.push(node.route);
      }

      if (node.children.length > 0) {
        walk(node.children);
      }
    });
  };

  walk(nodes);
  return routes;
}
